// 主人公の通常攻撃の時間割 (振り始めからの ms)。
//   連撃を切っている間 (配布版の既定) と見切りカウンターは balance.ts の PLAYER の値 = 連撃を作る前と同じ。
//   連撃を試す間 (DEV ?comboTrial=1) は、速さと「1振りの中の配分」の3案を ?attackTempo=A|B|C で選ぶ。
//   2026-10-02 ユーザー「もしかしたら変更前の速度位が正しくて、ただ1モーションあたりの時間の使い方に問題があったのかも知れない」
//   → 他作品の調べ (docs/handoffs/2026-10-01-player-attack-and-fx.md「10-02 午後」):
//      Nine Sols 1・2段目 = 振りかぶり0.10s・次の振りまで約0.35s / 3段目 = 振りかぶり0.25s・後0.43s。
//      Elden Ring = 1振りの約7割が当たる前の溜めで、当たり後すぐ次へ繋がる。
//      うちの旧配分 (C) は溜め60ms・当たり後260ms で逆。
import { PLAYER } from './balance'
import type { Player } from './types'
import { playerComboEnabled } from './player_combo_switch'

export interface AttackStepTiming {
  /** 当たりの窓 [activeStartMs, activeEndMs)。 振り始め〜activeStartMs はパリィへ切り替えられる。 */
  activeStartMs: number
  activeEndMs: number
  /** この時刻からまたパリィへ切り替えられる。 */
  parryReopenMs: number
  /** 攻撃中に押してあった次の段がこの時刻に出る (= 押していなければ idle へ戻る時刻)。 */
  totalMs: number
  /** 3段目の前進が始まる時刻 (〜activeEndMs の smoothstep)。 無ければ 0。 */
  lungeStartMs?: number
}

export type AttackTempo = 'A' | 'B' | 'C'

// A = Nine Sols 寄り / B = 中間 / C = 変更前 (今の配布版と同じ配分。3段目だけ振り終わりの隙+120ms)。
//   パリィへ戻れるのは A・B は当たりの窓の60ms後、C は旧と同じ260ms。
export const ATTACK_TEMPOS: Record<AttackTempo, { light: AttackStepTiming, heavy: AttackStepTiming }> = {
  A: {
    light: { activeStartMs: 100, activeEndMs: 160, parryReopenMs: 220, totalMs: 350 },
    heavy: { activeStartMs: 250, activeEndMs: 310, parryReopenMs: 370, totalMs: 680 },
  },
  B: {
    light: { activeStartMs: 160, activeEndMs: 220, parryReopenMs: 280, totalMs: 420 },
    // 3段目: 10-02 ユーザー「3段目はより重たい攻撃だからための時間の割合を上げるべき」
    //   → 溜めの割合を 1・2段目と同じ 38% (230/600) から 50% (340/680) へ。
    //   前進は跳ぶ所 (260ms〜) だけ。 溜めの間は体 (当たり判定) を動かさない = Spine attack_3 の跳びと一致。
    //   10-04 ユーザー「ゲームで遊んで確かめたいから本番の3段目にして」= DS2 の刺剣「後ろステップ→突き」版。
    //   突くまでと当たりは同じ、伸び切りで止まる (〜660ms)・戻る (〜1000ms) を延ばした (旧 totalMs 680)。
    heavy: { activeStartMs: 340, activeEndMs: 400, parryReopenMs: 460, totalMs: 1000, lungeStartMs: 260 },
  },
  C: {
    light: { activeStartMs: 60, activeEndMs: 140, parryReopenMs: 260, totalMs: 400 },
    heavy: { activeStartMs: 60, activeEndMs: 140, parryReopenMs: 260, totalMs: 520 },
  },
}

// 既定は B (10-02 ユーザー「AよりBの方が良い」)。
let tempo: AttackTempo = 'B'
export function attackTempo(): AttackTempo { return tempo }
export function setAttackTempo(value: string | null): void {
  if (value === 'A' || value === 'B' || value === 'C') tempo = value
}

const SINGLE: AttackStepTiming = {
  activeStartMs: PLAYER.attackActiveStartMs,
  activeEndMs: PLAYER.attackActiveEndMs,
  parryReopenMs: PLAYER.attackParryCancelReopenMs,
  totalMs: PLAYER.attackActiveEndMs + PLAYER.attackRecoveryMs,
}

/** 段 (0〜2) の時間割。 連撃を切っている間は段に関わらず1本の攻撃 (PLAYER の値)。 */
export function attackStepTiming(comboStep = 0, isCounter = false): AttackStepTiming {
  if (!playerComboEnabled() || isCounter) return SINGLE
  const t = ATTACK_TEMPOS[tempo]
  return comboStep === PLAYER.attackComboSteps - 1 ? t.heavy : t.light
}

export function attackTimingOf(player: Pick<Player, 'attackComboStep' | 'attackIsCounter'>): AttackStepTiming {
  return attackStepTiming(player.attackComboStep ?? 0, player.attackIsCounter)
}

/** 3段目の前進の進み具合 0〜1 (lungeStartMs〜activeEndMs の smoothstep)。 */
export function heavyLungeProgress(comboStep: number, elapsedMs: number): number {
  const t = attackStepTiming(comboStep)
  const from = t.lungeStartMs ?? 0
  const s = Math.max(0, Math.min(1, (elapsedMs - from) / Math.max(1, t.activeEndMs - from)))
  return s * s * (3 - 2 * s)
}

/**
 * 描いたコマは旧の時間割 (C) に合わせて作ってある。 今の時間割の経過 ms を、溜め・当たり・戻りの
 * 3区間ごとに旧の時間割へ写す (溜めが長い案では溜めのコマがその分ゆっくり進む)。
 */
export function toFrameTimelineMs(comboStep: number, elapsedMs: number): number {
  const now = attackStepTiming(comboStep)
  const base = comboStep === PLAYER.attackComboSteps - 1 ? ATTACK_TEMPOS.C.heavy : ATTACK_TEMPOS.C.light
  const map = (t: number, a0: number, a1: number, b0: number, b1: number) =>
    b0 + (a1 > a0 ? (t - a0) / (a1 - a0) : 1) * (b1 - b0)
  if (elapsedMs < now.activeStartMs) return map(elapsedMs, 0, now.activeStartMs, 0, base.activeStartMs)
  if (elapsedMs < now.activeEndMs) return map(elapsedMs, now.activeStartMs, now.activeEndMs, base.activeStartMs, base.activeEndMs)
  if (elapsedMs < now.totalMs) return map(elapsedMs, now.activeEndMs, now.totalMs, base.activeEndMs, base.totalMs)
  return base.totalMs + (elapsedMs - now.totalMs)
}
